import { ReactNode } from "react";
import { Box } from "@mui/material";
import { DotLegalCheckbox, DotLegalHeader, DotLegalMultiSelect, DotLegalSelect, DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import DotLegalSection from "@common/components/dotLegalSection/DotLegalSection";
import RiskLevelChip from "../RiskLevelChip";
import { RiskAreaMatrix } from "../../riskSettings/riskArea/riskAreaDialog/riskAreaMatrix/RiskAreaMatrix.tsx";
import { RiskScoringForm as RiskScoringFormState } from "./useRiskScoringForm";
import { useRiskScoringFormStyles } from "./RiskScoringForm.styles";
import { RiskExposureMode } from "../riskExposure/RiskExposure.types";
import RiskParticipantsPicker from "./RiskParticipantsPicker";

interface IRiskScoringForm {
    form: RiskScoringFormState;
    // Optional per-card slots, used by the single-entity assess dialog for its carry-forward links.
    consequenceFooter?: ReactNode;
    probabilityFooter?: ReactNode;
    participantsFooter?: ReactNode;
}

// The consequence + probability cards, exposure, confidence and participants, and the read-only risk matrix, scored against a risk area. Shared by the
// single-entity assess dialog and the operational bulk-assess dialog; all state lives in useRiskScoringForm.
export function RiskScoringForm({ form, consequenceFooter, probabilityFooter, participantsFooter }: IRiskScoringForm) {
    const styles = useRiskScoringFormStyles();
    const { translateString } = useTranslation();

    if (form.riskArea === undefined) return null;

    return (
        <Box sx={styles.columns}>
            <Box sx={styles.leftColumn}>
                <DotLegalSection>
                    <Box sx={styles.column}>
                        <DotLegalHeader headerStyle="small" color="primary" marginBottom={0.5}>
                            {translateString("consequence")}
                        </DotLegalHeader>
                        <Box sx={{ mb: 1.5 }}>
                            <DotLegalSelect
                                label={translateString("consequenceLevel")}
                                noMargin
                                options={form.consequenceOptions}
                                selectedItem={form.consequence?.toString() ?? null}
                                onChange={(value) => form.setConsequence(value ? Number(value) : null)}
                                disableSearch
                                noOptionsLabel={translateString("noOptions")}
                                errorText={form.errors.consequence}
                            />
                        </Box>
                        <Box sx={{ mb: 1.5 }}>
                            <DotLegalMultiSelect
                                label={translateString("consequences")}
                                noMargin
                                options={form.consequenceTypeOptions}
                                chosenOptions={form.selectedConsequenceTypeIds}
                                onChange={form.setSelectedConsequenceTypeIds}
                                noOptionsLabel={translateString("noOptions")}
                            />
                        </Box>
                        <DotLegalTextField
                            debounce={false}
                            noMargin
                            multiline
                            rows={4}
                            label={translateString("consequenceJustification")}
                            value={form.consequenceJustification}
                            onChange={form.setConsequenceJustification}
                            errorText={form.errors.consequenceJustification}
                        />
                        {consequenceFooter}
                    </Box>
                </DotLegalSection>

                <DotLegalSection>
                    <Box sx={styles.column}>
                        <DotLegalHeader headerStyle="small" color="primary" marginBottom={0.5}>
                            {translateString("probability")}
                        </DotLegalHeader>
                        <Box sx={{ mb: 1.5 }}>
                            <DotLegalSelect
                                label={translateString("probabilityLevel")}
                                noMargin
                                options={form.probabilityOptions}
                                selectedItem={form.probability?.toString() ?? null}
                                onChange={(value) => form.setProbability(value ? Number(value) : null)}
                                disableSearch
                                noOptionsLabel={translateString("noOptions")}
                                errorText={form.errors.probability}
                            />
                        </Box>
                        <Box sx={{ mb: 1.5 }}>
                            <DotLegalMultiSelect
                                label={translateString("securityMeasures")}
                                noMargin
                                options={form.measureOptions}
                                chosenOptions={form.selectedMeasureIds}
                                onChange={form.setSelectedMeasureIds}
                                noOptionsLabel={translateString("noOptions")}
                            />
                        </Box>
                        <DotLegalTextField
                            debounce={false}
                            noMargin
                            multiline
                            rows={4}
                            label={translateString("probabilityJustification")}
                            value={form.probabilityJustification}
                            onChange={form.setProbabilityJustification}
                            errorText={form.errors.probabilityJustification}
                        />
                        {probabilityFooter}
                    </Box>
                </DotLegalSection>

                {form.exposureEnabled && (
                    <DotLegalSection>
                        <Box sx={styles.column}>
                            <DotLegalCheckbox
                                margin={{ marginLeft: -1 }}
                                checked={form.captureExposure}
                                onChange={form.setCaptureExposure}
                                label={translateString(
                                    form.exposureMode === RiskExposureMode.Financial ? "specifyExposureOptional" : "specifyExposureScaleOptional"
                                )}
                            />
                            {form.captureExposure && form.exposureMode === RiskExposureMode.Financial && (
                                <Box sx={{ marginRight: 4 }}>
                                    <DotLegalTextField
                                        debounce={false}
                                        noMargin
                                        label={translateString("estimatedFinancialLoss", { currency: form.exposureCurrencyCode ?? "" })}
                                        toolTipText={translateString("exposureImpactHelpText")}
                                        value={form.exposureValueText}
                                        onChange={form.setExposureValueText}
                                        onlyNumber="decimal"
                                        minNumberValue={0}
                                    />
                                </Box>
                            )}
                            {form.captureExposure && form.exposureMode === RiskExposureMode.Scale && (
                                <Box sx={{ marginRight: 4 }}>
                                    <DotLegalSelect
                                        noMargin
                                        label={translateString("estimatedExposureScale")}
                                        toolTipText={translateString("exposureImpactHelpText")}
                                        options={form.exposureScaleOptions}
                                        selectedItem={form.exposureValueText === "" ? null : form.exposureValueText}
                                        onChange={(value) => form.setExposureValueText(value ?? "")}
                                        disableSearch
                                        noOptionsLabel={translateString("noOptions")}
                                    />
                                </Box>
                            )}
                        </Box>
                    </DotLegalSection>
                )}

                <DotLegalSection>
                    <Box sx={styles.column}>
                        <DotLegalHeader headerStyle="small" color="primary" marginBottom={0.5}>
                            {translateString("assessmentConfidence")}
                        </DotLegalHeader>
                        <DotLegalSelect
                            label={translateString("assessmentConfidenceLevel")}
                            noMargin
                            options={form.confidenceOptions}
                            selectedItem={form.confidence?.toString() ?? null}
                            onChange={(value) => form.setConfidence(value ? Number(value) : null)}
                            disableSearch
                            noOptionsLabel={translateString("noOptions")}
                        />
                    </Box>
                </DotLegalSection>

                <DotLegalSection>
                    <Box sx={styles.column}>
                        <DotLegalHeader headerStyle="small" color="primary" marginBottom={0.5}>
                            {translateString("participants")}
                        </DotLegalHeader>
                        <RiskParticipantsPicker form={form} footer={participantsFooter} />
                    </Box>
                </DotLegalSection>
            </Box>

            <DotLegalSection>
                <Box sx={styles.column}>
                    <DotLegalHeader headerStyle="small" color="primary" marginBottom={0.5}>
                        {translateString("riskMatrix")}
                    </DotLegalHeader>
                    <Box sx={styles.calculatedRiskRow}>
                        <Box sx={styles.calculatedRiskLabel}>{translateString("calculatedRiskLevel")}:</Box>
                        {form.calculatedRisk != null && form.calculatedRiskStyle != null ? (
                            <RiskLevelChip
                                level={form.calculatedRisk}
                                label={form.calculatedRiskLabel}
                                color={form.calculatedRiskStyle.color}
                                icon={form.calculatedRiskStyle.icon}
                            />
                        ) : (
                            <Box sx={styles.unscored}>{translateString("riskAssessmentStatusUnscored")}</Box>
                        )}
                    </Box>

                    <RiskAreaMatrix
                        readOnly
                        scale={form.riskArea.scale}
                        consequenceLevels={form.riskArea.consequenceLevels}
                        probabilityLevels={form.riskArea.probabilityLevels}
                        riskLevelIndicators={form.riskArea.riskLevelIndicators}
                        riskLevels={form.riskArea.riskLevels}
                        matrix={form.riskArea.matrix}
                        selectedConsequence={form.consequence}
                        selectedProbability={form.probability}
                        onCellClick={form.onMatrixCellClick}
                    />
                </Box>
            </DotLegalSection>
        </Box>
    );
}

export default RiskScoringForm;
